import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { eventDays, todayInWarsaw } from '../events/calendar';
import { addDays, type EventView } from '../events/labels';
import { formatDayText } from '../events/fields';
import { useDebounced } from '../kits/KitEditor';
import { FUNCTIONS_RESOURCE, PEOPLE_KEY, PERSONS_RESOURCE, type FunctionSummary, type Person } from '../people/types';
import { RowMenu } from '../ui/RowMenu';
import { AnswersBadge } from '../requests/AnswersBadge';
import { DateQuestionDialog } from '../requests/DateQuestionDialog';
import { shortDateTime } from '../requests/types';
import './crew.css';
import { ASSIGNMENT_STATUSES, candidates, CREW_RESOURCE, dayShort, hoursToInstants, missingGrants, type Assignment, type AssignmentStatus } from './types';
import { blockedDays, unavailableLabel, useUnavailable } from './unavailable';
import { useCrewEvent } from './useCrewEvent';
import { refreshCrew } from './refreshCrew';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard, validForm } from '../ui/unsaved';
import { selfMarkAsk } from '../availability/peopleGrid';
import { useSelfMarkAsk } from '../availability/SelfMarkDialog';

/** Polish message of a failed request; validation errors list their fields' messages. */
function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
  return details.length > 0 ? details.join('. ') : error.message;
}

/** The event's crew ("Obsada" tab of the event card): who works, in which function, when, and whether they confirmed. */
export function CrewTab({ event }: { event: EventView }) {
  const { me } = useMe();
  // the crew, the answers, the places with ⓘ Info, the quote's bar and the dialogs — shared with the grid's side panel
  const { crew, list, refresh, crewQuote, requests, asked, askedOf, crewPeople, inCrew, maySend, setSending, setConfirming, confirmBlocked, positions, dialogs } = useCrewEvent(event);
  const [adding, setAdding] = useState(false);
  // stage 2: „Poproś o dostępność” after a refused add (the person is „Niedostępny”)
  const [askAvailability, setAskAvailability] = useState<{ personId: string; personName: string; functionId: string } | null>(null);

  // K: „Oznacz jako obsadzone” (only with the setting „także ręcznie”)
  const markStaffed = useMutation({ mutationFn: () => api(`/api/crew/events/${event.id}/staffed`, { method: 'POST' }), onSuccess: refresh });

  if (!me) return null;
  const askedOutside = asked.filter((r) => !inCrew.has(r.personId));
  const mayEdit = can(me, CREW_RESOURCE, 'edit') && fieldVisible(me, CREW_RESOURCE, 'status');
  const mayAdd = can(me, CREW_RESOURCE, 'create');

  return (
    <div role="tabpanel" aria-label="Obsada" className="crew-tab" data-feature-id="crew.assign">
      {/* owner 2026-10-22: a quiet "+" beside the section label instead of a heavy black "Dodaj osobę" at the bottom */}
      <div className="crew-head">
        <h3 className="crew-label">
          Osoby w obsadzie{crewPeople.length > 0 && <span className="crew-count">{crewPeople.length}</span>}
        </h3>
        {(mayAdd || maySend) && !adding && (
          <div className="crew-head-actions">
            {maySend && (
              <button className="btn ghost small" type="button" data-feature-id="requests.send" onClick={() => setSending({ afterAdd: false })}>
                Wyślij zapytanie
              </button>
            )}
            {maySend && list.some((a) => a.status !== 'odmowa') && (
              // the owner: rarer actions in „…” — the confirmation to the whole crew, by hand
              <RowMenu label="Więcej działań obsady" featureId="requests.confirmation" actions={[{ label: 'Wyślij potwierdzenie do obsady', onSelect: () => setConfirming(true), disabled: !!confirmBlocked, title: confirmBlocked ?? undefined }]} />
            )}
            {mayAdd && (
              <button className="btn icon plus-btn" type="button" aria-label="Dodaj osobę" title="Dodaj osobę" onClick={() => setAdding(true)}>
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
      {crew.data?.datesChanged && (
        // owner's Q3: the dates changed — the crew is asked again by hand (nothing was sent to them)
        <p className="crew-dates-changed" role="status" data-feature-id="crew.people_availability">
          <strong>Zmieniono datę wydarzenia — zapytaj obsadę ponownie.</strong> Osoby wolne w nowych dniach wróciły na „Proponowany”; kolizje i niedostępność są na czerwono.
        </p>
      )}
      {crew.data?.unstaffed && (
        // „Dostępność → Ludzie”: someone withdrew their availability and nobody of that function came in after
        <p className="crew-unstaffed" role="status" data-feature-id="crew.people_availability">
          <strong>Nieobsadzone</strong>
          {(crew.data.unstaffedDays ?? []).length > 0 ? ` (${crew.data.unstaffedDays!.map(dayShort).join(', ')})` : ''} — ktoś wycofał dostępność albo robi tego dnia inne wydarzenie. Dodaj osobę w tej funkcji.
          {crew.data.unstaffedManual && mayEdit && (
            <button className="btn ghost small" type="button" disabled={markStaffed.isPending} onClick={() => markStaffed.mutate()}>
              Oznacz jako obsadzone
            </button>
          )}
        </p>
      )}
      {(requests.data?.confirmations ?? []).length > 0 && (
        <p className="muted crew-confirmations" data-feature-id="requests.confirmation">
          {requests.data!.confirmations!.map((c) => `Potwierdzenie do obsady wysłane${c.sentAt ? ` ${shortDateTime(c.sentAt)}` : ''} (${[c.sentBy, `${c.people} ${c.people === 1 ? 'osoba' : 'osób'}`].filter(Boolean).join(', ')})`).join(' · ')}
        </p>
      )}
      {crew.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {crew.error && <p className="alert" role="alert">{errorText(crew.error, 'Nie udało się wczytać obsady')}</p>}
      {/* owner 2026-10-26: ONLY the list of places (with „ⓘ Info” for the numbers) — the crew list of before is gone */}
      {crewQuote.notice}
      {positions(!adding)}
      {askedOutside.length > 0 && (
        <section className="crew-asked" aria-label="Zapytani spoza obsady">
          <h3>Zapytani spoza obsady</h3>
          <ul>
            {askedOutside.map((r) => (
              <li key={r.id}>
                <span className="crew-person">{r.personName ?? 'Osoba'}</span>
                <AnswersBadge recipient={r} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {mayAdd && adding && (
        <AddForm
          event={event}
          taken={list}
          {...(maySend ? { onAskAvailability: (p: { personId: string; personName: string; functionId: string }) => setAskAvailability(p) } : {})}
          onDone={(added) => {
            setAdding(false);
            // a new person in the crew, not asked yet: offer the availability request ("Nie" blocks nothing); the
            // question about the quote comes after it (one dialog at a time)
            if (added?.isNew && maySend && !askedOf.has(added.personId)) setSending({ added: { personId: added.personId, personName: added.personName }, afterAdd: true });
            else if (added) void crewQuote.ask();
          }}
        />
      )}
      {askAvailability && (
        <DateQuestionDialog
          title="Poproś o dostępność"
          people={[{ personId: askAvailability.personId, name: askAvailability.personName }]}
          days={eventDayList(event)}
          event={{ id: event.id, name: event.name || 'Wydarzenie' }}
          functionId={askAvailability.functionId}
          onClose={() => setAskAvailability(null)}
        />
      )}
      {dialogs}
    </div>
  );
}

/** Stage 2: the event's days from today on — what „Poproś o dostępność” may ask. */
function eventDayList(event: EventView): string[] {
  if (!event.startsAt || !event.endsAt) return [];
  const { first, last } = eventDays({ startsAt: event.startsAt, endsAt: event.endsAt });
  const today = todayInWarsaw();
  const days: string[] = [];
  for (let d = first; d <= last && days.length < 62; d = addDays(d, 1)) if (d >= today) days.push(d);
  return days;
}

/** The days the hours of a partial assignment can be on: the day before the event (setup) to its last day. */
function daysAround(event: EventView): string[] {
  if (!event.startsAt || !event.endsAt) return [];
  const { first, last } = eventDays({ startsAt: event.startsAt, endsAt: event.endsAt });
  const days: string[] = [];
  for (let d = addDays(first, -1); d <= last && days.length < 60; d = addDays(d, 1)) days.push(d);
  return days;
}

/**
 * "Dodaj osobę": function first, then the people who can do it (and everybody else); hours only when not the whole event.
 * `initialFunctionId`: the function chosen already (rules in the quote: „Dodaj” of a crew rule's suggestion).
 */
export function AddForm({
  event,
  taken,
  onDone,
  initialFunctionId,
  onAskAvailability,
}: {
  event: EventView;
  taken: Assignment[];
  onDone: (added?: { personId: string; personName: string; isNew: boolean }) => void;
  initialFunctionId?: string;
  /** Stage 2: the person is „Niedostępny” (422) — „Poproś o dostępność” for them, this function. */
  onAskAvailability?: (p: { personId: string; personName: string; functionId: string }) => void;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [functionId, setFunctionId] = useState(initialFunctionId ?? '');
  const [personId, setPersonId] = useState('');
  const [status, setStatus] = useState<AssignmentStatus>('proponowany');
  const [partial, setPartial] = useState(false);
  const [dialog, setDialog] = useState<'function' | 'person' | null>(null);
  const days = daysAround(event);
  const firstDay = event.startsAt && event.endsAt ? eventDays({ startsAt: event.startsAt, endsAt: event.endsAt }).first : '';
  const [day, setDay] = useState(firstDay);
  const [from, setFrom] = useState('10:00');
  const [to, setTo] = useState('14:00');

  const mayFunctions = !!me && can(me, FUNCTIONS_RESOURCE, 'view');
  const mayPersons = !!me && can(me, PERSONS_RESOURCE, 'view');
  // production 2026-10-26 / review L5: who is „Niedostępny” for this work (own hours with „Tylko część wydarzenia”)
  const ownHours = partial && day && from && to ? hoursToInstants(day, from, to) : null;
  const unavailable = useUnavailable(event.id, mayPersons, ownHours);

  const functions = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], enabled: mayFunctions, queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
  // everybody (the server sends at most 200: then a search field narrows it), and separately who has the function
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 250);
  const candidatesKey = [...PEOPLE_KEY, 'persons', 'crew-candidates', q];
  const persons = useQuery({
    queryKey: candidatesKey,
    enabled: mayPersons,
    placeholderData: keepPreviousData,
    queryFn: () => api<{ persons: Person[]; truncated?: boolean }>(`/api/people/persons${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });
  const withFn = useQuery({
    queryKey: [...PEOPLE_KEY, 'persons', 'function', functionId],
    enabled: mayPersons && functionId !== '',
    queryFn: () => api<{ persons: Person[] }>(`/api/people/persons?functionId=${encodeURIComponent(functionId)}`),
  });
  const already = new Set(taken.filter((a) => a.functionId === functionId).map((a) => a.personId));

  const fnList = functions.data?.functions ?? [];
  const fnName = fnList.find((f) => f.id === functionId)?.name ?? 'Funkcja';
  const split = candidates(persons.data?.persons ?? [], functionId);
  // owner 21:55: only people with the function; a search narrows the loaded list
  const withFunction = q ? split.withFunction : (withFn.data?.persons ?? split.withFunction);
  const truncated = persons.data?.truncated === true;
  const isNewRef = useRef(false);
  const personName = withFunction.find((p) => p.id === personId)?.name ?? 'Osoba';
  const mayNewFunction = !!me && can(me, FUNCTIONS_RESOURCE, 'create');
  // review L6: a new person here gets the chosen function — without the visible field „Funkcje” it could not, and a
  // person without the function cannot join the crew in it (owner 21:55): no „+ Nowa osoba” then
  const mayNewPerson = !!me && can(me, PERSONS_RESOURCE, 'create') && fieldVisible(me, PERSONS_RESOURCE, 'functions');

  // review I2: added as „Odmowa” over the person's own mark — „Wpis pracownika” first, then with the token
  const selfAsk = useSelfMarkAsk();
  const add = useMutation({
    mutationFn: async (consulted?: string) => {
      isNewRef.current = !taken.some((a) => a.personId === personId); // not in the crew before (another function does not count)
      return api<Assignment>(`/api/crew/events/${event.id}/assignments`, {
        method: 'POST',
        body: { personId, functionId, ...(status !== 'proponowany' ? { status } : {}), ...(partial ? hoursToInstants(day, from, to) : {}), ...(consulted ? { consulted } : {}) },
      });
    },
    onError: (e) => {
      selfAsk.offer(e, (consulted) => add.mutate(consulted));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: PEOPLE_KEY }),
    onSuccess: async (a) => {
      await refreshCrew(queryClient);
      onDone({ personId, personName: a.personName ?? personName, isNew: isNewRef.current });
    },
  });

  // owner 2026-10-23 21:14: a function or person chosen and not added: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: functionId !== (initialFunctionId ?? '') || personId !== '',
    save: async (el) => {
      if (!validForm(el)) return false;
      await add.mutateAsync();
      return true;
    },
  });
  if (!me) return null;
  const missing = missingGrants(me);
  if (missing.length > 0) {
    return (
      <p className="notice">
        Żeby dodać osobę, potrzebujesz prawa podglądu „Osoby” i „Funkcje”. W edytorze uprawnień roli nadaj: {missing.join(' i ')}.
      </p>
    );
  }
  // production 2026-10-26: „Niedostępny” on a day the assignment covers (the whole event, or the chosen day) — greyed
  const coveredDays = partial ? [day] : eventDayList(event);  // the server checks the hours; these are the days to show
  const personOption = (p: Person) => {
    const hit = blockedDays(unavailable, p.id, coveredDays);
    return (
      <option key={p.id} value={p.id} disabled={already.has(p.id) || hit.length > 0}>
        {hit.length > 0 ? unavailableLabel(p.name ?? 'Osoba', hit) : (p.name ?? 'Osoba')}
        {already.has(p.id) ? ' (już w obsadzie)' : ''}
      </option>
    );
  };
  return (
    <>
      <form
        ref={guardRef}
        className="crew-add"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate(undefined);
        }}
      >
        <h3>Dodaj osobę</h3>
        {functions.data && fnList.length === 0 && (
          <p className="notice">
            Nie ma jeszcze żadnych funkcji.{' '}
            {mayNewFunction ? 'Dodaj pierwszą przyciskiem „+ Nowa funkcja”.' : 'Funkcje dodaje ktoś z prawem „Funkcje: Dodawanie” (Osoby → Funkcje).'}
          </p>
        )}
        <div className="field-row">
          <div className="field">
            <label htmlFor="crew-add-function">Funkcja</label>
            <select
              id="crew-add-function"
              value={functionId}
              onChange={(e) => {
                setFunctionId(e.target.value);
                setPersonId('');
              }}
            >
              <option value="">Wybierz funkcję</option>
              {fnList.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name ?? 'Funkcja'}
                </option>
              ))}
            </select>
            <span className="crew-add-links">
              {mayNewFunction && (
                <button className="btn ghost small" type="button" onClick={() => setDialog('function')}>
                  + Nowa funkcja
                </button>
              )}
              <Link to="/osoby/funkcje">Zarządzaj funkcjami</Link>
            </span>
          </div>
          <div className="field">
            <label htmlFor="crew-add-person">Osoba</label>
            <select id="crew-add-person" value={personId} disabled={functionId === ''} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">{functionId === '' ? 'Najpierw wybierz funkcję' : persons.data && withFunction.length === 0 ? `Nikt nie ma funkcji „${fnName}”` : 'Wybierz osobę'}</option>
              {/* owner 21:55: only people with the function (no technician on a realizator's place) */}
              {withFunction.map(personOption)}
            </select>
            {(truncated || search !== '') && (
              <input
                type="search"
                className="crew-person-search"
                aria-label="Szukaj osoby"
                placeholder="Szukaj: imię, nazwisko, telefon"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPersonId('');
                }}
                autoComplete="off"
              />
            )}
            {truncated && <span className="crew-add-hint">Lista obcięta — wpisz nazwisko.</span>}
            {mayNewPerson && (
              <span className="crew-add-links">
                <button className="btn ghost small" type="button" disabled={functionId === ''} onClick={() => setDialog('person')}>
                  + Nowa osoba
                </button>
              </span>
            )}
          </div>
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as AssignmentStatus)}>
              {(Object.keys(ASSIGNMENT_STATUSES) as AssignmentStatus[]).map((s) => (
                <option key={s} value={s}>
                  {ASSIGNMENT_STATUSES[s]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {functionId !== '' && persons.data && withFunction.length === 0 && (
          <p className="muted">Nikt nie ma jeszcze funkcji „{fnName}”. Dopisz ją osobie w Osoby (profil) albo dodaj nową osobę.</p>
        )}
        <label className="check">
          <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
          <span>Tylko część wydarzenia (np. sam montaż)</span>
        </label>
        {partial && (
          <div className="field-row">
            <label className="field">
              <span>Dzień</span>
              <select value={day} onChange={(e) => setDay(e.target.value)}>
                {days.map((d) => (
                  <option key={d} value={d}>
                    {formatDayText(d)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Od</span>
              <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} required />
            </label>
            <label className="field">
              <span>Do</span>
              <input type="time" value={to} onChange={(e) => setTo(e.target.value)} required />
            </label>
          </div>
        )}
        {selfAsk.dialog}
        {add.error && !selfMarkAsk(add.error) && (
          <p className="alert" role="alert">
            {errorText(add.error, 'Nie udało się dodać osoby')}
            {onAskAvailability && add.error instanceof ApiError && add.error.code === 'person_unavailable' && (
              <>
                {' '}
                <button className="btn ghost small" type="button" data-feature-id="requests.dates" onClick={() => onAskAvailability({ personId, personName, functionId })}>
                  Poproś o dostępność
                </button>
              </>
            )}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={functionId === '' || personId === '' || add.isPending}>
            {add.isPending ? 'Dodawanie…' : 'Dodaj do obsady'}
          </button>
          <button className="btn" type="button" onClick={() => onDone()}>
            Anuluj
          </button>
        </div>
      </form>
      {dialog === 'function' && (
        <NewFunctionDialog
          onClose={() => setDialog(null)}
          onCreated={(f) => {
            queryClient.setQueryData<{ functions: FunctionSummary[] }>([...PEOPLE_KEY, 'functions'], (prev) => ({ functions: [...(prev?.functions ?? []), f] }));
            void queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
            setFunctionId(f.id);
            setPersonId('');
            setDialog(null);
          }}
        />
      )}
      {dialog === 'person' && (
        <NewPersonDialog
          functionId={functionId}
          functionName={fnName}
          onClose={() => setDialog(null)}
          onCreated={(p) => {
            queryClient.setQueryData<{ persons: Person[] }>(candidatesKey, (prev) => ({ ...prev, persons: [...(prev?.persons ?? []), p] }));
            // owner 21:55: the list offers only people with the function — the new person has it
            queryClient.setQueryData<{ persons: Person[] }>([...PEOPLE_KEY, 'persons', 'function', functionId], (prev) => ({ ...prev, persons: [...(prev?.persons ?? []), p] }));
            void queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
            setPersonId(p.id);
            setDialog(null);
          }}
        />
      )}
    </>
  );
}

/** "+ Nowa funkcja" from the crew tab: just the name; rates and tasks are set later in Osoby → Funkcje. */
function NewFunctionDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (f: FunctionSummary) => void }) {
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<FunctionSummary>('/api/people/functions', { method: 'POST', body: { name: name.trim() } }),
    onSuccess: (f) => onCreated({ ...f, taskCount: 0 }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const guardRef = useUnsavedGuard({
    dirty: name.trim() !== '',
    save: async () => {
      await create.mutateAsync();
      return true;
    },
  });
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="crew-new-function-title" onSubmit={submit}>
        <DialogLayer onClose={onClose} busy={create.isPending} />
        <h2 id="crew-new-function-title">Nowa funkcja</h2>
        <label className="field">
          <span>Nazwa</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} autoComplete="off" autoFocus placeholder="np. Realizator FOH" />
        </label>
        <p className="muted">Stawkę i listę zadań ustawisz później w Osoby → Funkcje.</p>
        {create.error && (
          <p className="alert" role="alert">
            {errorText(create.error, 'Nie udało się dodać funkcji')}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={create.isPending || name.trim() === ''}>
            {create.isPending ? 'Dodawanie…' : 'Dodaj funkcję'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

/** "+ Nowa osoba" from the crew tab: name and contact only, with the chosen function (the rest in Osoby). */
function NewPersonDialog({ functionId, functionName, onClose, onCreated }: { functionId: string; functionName: string; onClose: () => void; onCreated: (p: Person) => void }) {
  const { me } = useMe();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const show = (field: string) => !!me && fieldVisible(me, PERSONS_RESOURCE, field);
  const withFunction = functionId !== '' && show('functions');
  const create = useMutation({
    mutationFn: () =>
      api<Person>('/api/people/persons', {
        method: 'POST',
        body: {
          name: name.trim(),
          ...(show('phone') && phone.trim() ? { phone: phone.trim() } : {}),
          ...(show('email') && email.trim() ? { email: email.trim() } : {}),
          ...(withFunction ? { functions: [{ functionId }] } : {}),
        },
      }),
    onSuccess: onCreated,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const guardRef = useUnsavedGuard({
    dirty: name.trim() !== '' || phone.trim() !== '' || email.trim() !== '',
    save: async (el) => {
      if (!validForm(el)) return false;
      await create.mutateAsync();
      return true;
    },
  });
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="crew-new-person-title" onSubmit={submit}>
        <DialogLayer onClose={onClose} busy={create.isPending} />
        <h2 id="crew-new-person-title">Nowa osoba</h2>
        <label className="field">
          <span>Imię i nazwisko</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoComplete="off" autoFocus />
        </label>
        {show('phone') && (
          <label className="field">
            <span>Telefon</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
          </label>
        )}
        {show('email') && (
          <label className="field">
            <span>E-mail</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </label>
        )}
        <p className="muted">{withFunction ? `Osoba dostanie funkcję „${functionName}”. ` : ''}Zatrudnienie, stawki i konto ustawisz później w Osoby.</p>
        {create.error && (
          <p className="alert" role="alert">
            {errorText(create.error, 'Nie udało się dodać osoby')}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={create.isPending || name.trim() === ''}>
            {create.isPending ? 'Dodawanie…' : 'Dodaj osobę'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
